import { Injectable } from '@angular/core'
import { CurrentUser, GetCurrentUserQuery } from '@app/shared/services/current-user'
import { Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { AppState } from '@app/app.state'
import { Store } from '@ngrx/store'
import { IAccountData, IUser, IUserData, OrgUserStateTypes, ThemeTypes } from '@app/shared/data-types/user.interface'
import { loadUsers } from '@app/users-module/state/users.actions'
import { loadOrganizations } from '@app/organizations-module/state/organizations.actions'
import { IOrganizationRole, IOrganizationUser } from '@app/shared/data-types/organization.interface'
import { loadLAF } from '@app/shared/services/current-user/states/laf/laf.actions'
import { ILAF } from '@app/shared/data-types/laf.interface'
import { LayoutsService } from '@app/shared/services/layouts'
import { UsersStoreFacade } from '@app/users-module/state/users-store.facade'
import { insertOrganizationsRoles } from '@app/organization-roles-module/state/organization-roles.actions'
import { JWTTokenService } from '@app/shared/services/jwt-token'
import { BreakpointsService } from '../breakpoints'
import { GetIdentityQuery } from '@app/identities-module/core/queries/get-identity.query'
import { ThemeService } from '../theme'
import { IIdentity } from '@app/identities-module/core/data-types/identity.interface'

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  constructor(
    private jwtTokenService: JWTTokenService,
    private currentUser: CurrentUser,
    private layoutsService: LayoutsService,
    private getCurrentUserQuery: GetCurrentUserQuery,
    private getIdentityQuery: GetIdentityQuery,
    private users: UsersStoreFacade,
    private themeService: ThemeService,
    private breakpointsService: BreakpointsService,
    private store: Store<AppState>,
    private router: Router
  ) {}

  init(): Observable<IAccountData> {
    const token = this.jwtTokenService.get()

    if (!token) {
      return this.handleError()
    }

    return this.getIdentityQuery.call().pipe(
      switchMap((response: IIdentity) => {
        if (!response.id) {
          return this.handleError()
        }
        if (!response.agreement_accepted_at || !response.pdp_accepted_at) {
          return this.handleAgreementError()
        }
        return this.getCurrentUserQuery.call(response.id).pipe(
          tap((userDataResponse) => this.createStore(userDataResponse)),
          catchError(this.handleError.bind(this))
        )
      })
    )
  }

  private handleError(): Observable<IAccountData> {
    const emptyUser = <IAccountData>{}
    this.initLayout(emptyUser)
    // this.initTheme(ThemeTypes.SYSTEM)
    this.assignCurrentUserData(emptyUser, [])
    return of(emptyUser)
  }

  private handleAgreementError(): Observable<IAccountData> {
    const emptyUser = <IAccountData>{}
    this.initLayout(emptyUser)
    this.initTheme(ThemeTypes.SYSTEM)
    this.assignCurrentUserData(emptyUser, [])
    this.router.navigate(['/identities/agreement'])
    return of(emptyUser)
  }

  private createStore(userDataResponse: IAccountData): void {
    this.initLayout(userDataResponse)
    this.createOrganizationsStore(userDataResponse)
    this.createOrganizationsRolesStore(userDataResponse)
    this.createUsersStore(userDataResponse, this.buildOrganizationsUsers(userDataResponse))
    this.initTheme(userDataResponse.theme_type)
  }

  private assignCurrentUserData(userData: IAccountData, users: IUser[]): void {
    const user = users.find((u) => u.id == userData.id) || <IUser>{}
    Object.assign(this.currentUser, userData, user)

    this.users.getUserById(userData.id).subscribe((user) => {
      Object.assign(this.currentUser, user)
    })
  }

  private initTheme(theme: ThemeTypes): void {
    this.themeService.set(theme)
  }

  private initLayout(userData: IAccountData): void {
    const layout = this.layoutsService.getInitLayout(userData)
    this.layoutsService.init(layout)
    this.breakpointsService.init()
  }

  private createOrganizationsRolesStore(userData: IAccountData): void {
    let organizationsRoles: IOrganizationRole[] = []

    userData.organizations.forEach((o) => {
      organizationsRoles = organizationsRoles.concat(o.roles.map((r) => ({ ...r, organization_id: o.id })))
    })

    this.store.dispatch(insertOrganizationsRoles({ organizationsRoles }))
  }

  private createLAFStore(userData: IUserData): void {
    const laf = userData.look_and_feel as ILAF
    this.store.dispatch(loadLAF({ laf }))
  }

  private createUsersStore(userData: IAccountData, organizationsUsers: IOrganizationUser[]): void {
    const users = userData.users.map((u) => {
      const orgUser = organizationsUsers.find((ou) => ou.user_id === u.id)

      return {
        ...u,
        email: u.email?.includes('@demo.glabix.com') ? 'sandbox@demo.glabix.com' : u.email,
        org_state: orgUser?.state || OrgUserStateTypes.REVOKED,
        organization_ids: orgUser?.organization_ids || [],
        organization_colors: orgUser?.organization_colors || {},
        organization_initials: orgUser?.organization_initials || {},
        organization_role_ids: orgUser?.organization_role_ids || {},
        organization_statuses: orgUser?.organization_statuses || {},
      }
    })

    this.assignCurrentUserData(userData, users)
    this.store.dispatch(loadUsers({ users }))
  }

  private createOrganizationsStore(userData: IAccountData): void {
    const organizations = userData.organizations || []
    this.store.dispatch(loadOrganizations({ organizations }))
  }

  private buildOrganizationsUsers(userData: IAccountData): IOrganizationUser[] {
    let organizationsUsers: IOrganizationUser[] = []

    userData.organizations.forEach((o) => {
      const orgId = o.id

      o.organization_users.forEach((user) => {
        const userId = user.user_id

        if (!organizationsUsers.find((ou) => ou.user_id == userId)) {
          organizationsUsers.push({
            id: user.id,
            state: user.state,
            user_id: userId,
            created_at: user.created_at,
            created_by_id: user.created_by_id,
            organization_ids: [orgId],
            organization_colors: { [orgId]: user.color },
            organization_initials: { [orgId]: user.initials },
            organization_role_ids: { [orgId]: user.role_id },
            organization_statuses: { [orgId]: user.state },
          })
        } else {
          organizationsUsers = organizationsUsers.map((u) => {
            if (u.user_id !== userId) {
              return { ...u }
            }

            return {
              ...u,
              organization_ids: u.organization_ids.concat([orgId]),
              organization_colors: Object.assign(u.organization_colors, { [orgId]: user.color }),
              organization_initials: Object.assign(u.organization_initials, { [orgId]: user.initials }),
              organization_role_ids: Object.assign(u.organization_role_ids, { [orgId]: user.role_id }),
              organization_statuses: Object.assign(u.organization_statuses, { [orgId]: user.state }),
            }
          })
        }
      })
    })

    return organizationsUsers
  }
}
